import React from 'react';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import NavDropdown from 'react-bootstrap/NavDropdown';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { settingsAPI } from '../api/settingsAPI';
import { usePermissions } from '../contexts/PermissionContext';
import { useWarehouse, getWarehouseAbbreviation } from '../contexts/WarehouseContext';

function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const userToken = localStorage.getItem('userToken');
  const user_first_name = localStorage.getItem('user_first_name');
  const [theme, setTheme] = React.useState('light');
  const [expanded, setExpanded] = React.useState(false);
  const [systemSettings, setSystemSettings] = React.useState({
    enable_warehouse_transit: true, // default to true for safety
  });
  const { hasPermission, hasAnyPermission, isSuperuser, isPermissionAdmin } = usePermissions();
  const { currentWarehouse, warehouses, setCurrentWarehouse } = useWarehouse();

  const handleLogout = () => {
    localStorage.removeItem('userToken');
    localStorage.removeItem('username');
    localStorage.removeItem('user_first_name');
    navigate('/login');
  };

  const handleResetSettings = () => {
    localStorage.clear();
    window.location.reload();
  };

  const handleThemeChange = async (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);

    // Remove any existing vapor theme link elements
    const existingVaporLinks = document.querySelectorAll('link[data-theme="vapor"]');
    existingVaporLinks.forEach((link) => link.remove());

    if (newTheme === 'vapor') {
      // Create a link element for vapor theme instead of dynamic import
      const vaporLink = document.createElement('link');
      vaporLink.rel = 'stylesheet';
      vaporLink.href = '/static/css/bootswatch-vapor.css'; // We'll need to copy this file to public
      vaporLink.setAttribute('data-theme', 'vapor');

      // Add to head
      document.head.appendChild(vaporLink);
      document.documentElement.removeAttribute('data-bs-theme');
    } else {
      // For light/dark themes, just set the data attribute
      document.documentElement.setAttribute('data-bs-theme', newTheme);
    }
  };

  React.useEffect(() => {
    const storedTheme =
      localStorage.getItem('theme') ||
      (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
    handleThemeChange(storedTheme);
  }, []);

  // Add useEffect to fetch system settings
  React.useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await settingsAPI.get();
        setSystemSettings(settings);
      } catch (error) {
        console.error('Error fetching system settings:', error);
        // Keep default value if fetch fails
      }
    };
    fetchSettings();
  }, []);

  // Close navbar on navigation
  React.useEffect(() => {
    setExpanded(false);
  }, [location]);

  return (
    <Navbar
      expand="lg"
      fixed="top"
      bg="primary"
      variant="dark"
      expanded={expanded}
      onToggle={setExpanded}
    >
      <div className="container">
        <Navbar.Brand as={Link} to="/">
          <img
            src="/ltc_white_logo.png"
            height="40"
            className="d-inline-block align-top"
            alt="Liberty Trade Corp"
          />
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="navbarResponsive" />
        <Navbar.Collapse id="navbarResponsive">
          <Nav className="me-auto">
            {hasPermission('inventory.receive') && (
              <NavDropdown
                title={<i className="bi bi-lightning-fill"></i>}
                id="quick-actions-dropdown"
                data-bs-theme="light"
              >
                <NavDropdown.Item as={Link} to="/scan-barcode">
                  <i className="bi bi-box-arrow-in-down"></i>
                  <span className="ms-2">Receive Inventory</span>
                </NavDropdown.Item>
              </NavDropdown>
            )}
          </Nav>
          <Nav className="ms-auto">
            <Nav.Link as={Link} to="/dashboard">
              <i className="bi bi-pie-chart-fill"></i>
              <span className="ms-2">Dashboard</span>
            </Nav.Link>

            {hasPermission('ai.assistant') && (
              <Nav.Link as={Link} to="/ai-assistant">
                AI
              </Nav.Link>
            )}

            {hasAnyPermission([
              'locations.view',
              'vendors.view',
              'stock.categories.view',
              'stock.items.view',
              'inventory.view',
              'issued.view',
              'destinations.view',
              'stock.forecast.view',
              'recipes.view',
            ]) && (
              <NavDropdown title="Inventory" id="basic-nav-dropdown" data-bs-theme="light">
                {hasPermission('locations.view') && (
                  <NavDropdown.Item as={Link} to="/warehouses">
                    Warehouses
                  </NavDropdown.Item>
                )}
                {hasPermission('vendors.view') && (
                  <NavDropdown.Item as={Link} to="/vendors">
                    Vendors
                  </NavDropdown.Item>
                )}
                {hasPermission('stock.items.view') && (
                  <NavDropdown.Item as={Link} to="/brands">
                    Brands
                  </NavDropdown.Item>
                )}
                {hasAnyPermission(['stock.items.view', 'stock.categories.view']) && (
                  <NavDropdown.Divider />
                )}
                {hasPermission('stock.items.view') && (
                  <NavDropdown.Item as={Link} to="/stock-items">
                    Stock Items
                  </NavDropdown.Item>
                )}
                {hasPermission('stock.categories.view') && (
                  <NavDropdown.Item as={Link} to="/stock-item-categories">
                    Categories
                  </NavDropdown.Item>
                )}
                {hasPermission('stock.items.view') && (
                  <NavDropdown.Item as={Link} to="/item-families">
                    Item Families
                  </NavDropdown.Item>
                )}
                {hasPermission('recipes.view') && (
                  <NavDropdown.Item as={Link} to="/recipes">
                    Recipes
                  </NavDropdown.Item>
                )}
                {hasPermission('inventory.view') && (
                  <>
                    <NavDropdown.Divider />
                    <NavDropdown.Item as={Link} to="/inventory">
                      Inventory
                    </NavDropdown.Item>
                  </>
                )}
                {hasPermission('issued.view') && (
                  <NavDropdown.Item as={Link} to="/issued-inventory">
                    Issued Inventory
                  </NavDropdown.Item>
                )}
                {hasPermission('destinations.view') && (
                  <NavDropdown.Item as={Link} to="/issue-destinations">
                    Issue Destinations
                  </NavDropdown.Item>
                )}
                {systemSettings.enable_warehouse_transit && hasPermission('inventory.view') && (
                  <NavDropdown.Item as={Link} to="/inventory-transfers">
                    Transfers
                  </NavDropdown.Item>
                )}
                {hasPermission('inventory.view') && (
                  <NavDropdown.Item as={Link} to="/inventory-history">
                    History
                  </NavDropdown.Item>
                )}
                {hasPermission('stock.forecast.view') && (
                  <NavDropdown.Item as={Link} to="/stock-forecast">
                    Stock Forecast
                  </NavDropdown.Item>
                )}
              </NavDropdown>
            )}

            {hasPermission('po.view') && (
              <NavDropdown title="Sales" id="sales-purchasing-dropdown" data-bs-theme="light">
                <NavDropdown.Item as={Link} to="/purchase-orders">
                  <i className="bi bi-cart-check"></i>
                  <span className="ms-2">Purchase Orders</span>
                </NavDropdown.Item>
              </NavDropdown>
            )}

            {hasPermission('production.view') && (
              <NavDropdown title="Production" id="production-dropdown" data-bs-theme="light">
                <NavDropdown.Item as={Link} to="/productions">
                  Production LOTs
                </NavDropdown.Item>
              </NavDropdown>
            )}

            {hasPermission('reports.generate') && (
              <NavDropdown title="Reports" id="reports-dropdown" data-bs-theme="light">
                <NavDropdown.Item as={Link} to="/reports/issued-inventory">
                  <i className="bi bi-file-earmark-text"></i>
                  <span className="ms-2">Issued Inventory Report</span>
                </NavDropdown.Item>
              </NavDropdown>
            )}

            {/* separator */}
            <div className="nav-item py-2 py-lg-1 col-12 col-lg-auto ms-auto">
              <div className="vr d-none d-lg-flex h-100 mx-lg-2 text-white"></div>
              <hr className="d-lg-none my-2 text-white-50" />
            </div>

            {userToken ? (
              <NavDropdown
                title={
                  <>
                    Welcome, {user_first_name}
                    <i className="bi bi-person"></i>
                  </>
                }
                id="user-nav-dropdown"
                data-bs-theme="light"
              >
                <NavDropdown.Item as={Link} to="/profile">
                  <i className="bi bi-person-circle"></i>
                  <span className="ms-2">My Profile</span>
                </NavDropdown.Item>
                <NavDropdown.Divider />
                <NavDropdown.Item onClick={handleLogout}>
                  <i className="bi bi-box-arrow-in-right"></i>
                  <span className="ms-2">Logout</span>
                </NavDropdown.Item>
              </NavDropdown>
            ) : (
              <Nav.Link as={Link} to="/login"></Nav.Link>
            )}

            {/* separator */}
            <div className="nav-item py-2 py-lg-1 col-12 col-lg-auto">
              <div className="vr d-none d-lg-flex h-100 mx-lg-2 text-white"></div>
              <hr className="d-lg-none my-2 text-white-50" />
            </div>

            <NavDropdown
              align="end"
              data-bs-theme="light"
              title={
                <>
                  <i className="bi bi-gear"></i>
                  <span className="ms-2">Settings</span>
                </>
              }
              id="basic-nav-dropdown"
            >
              {hasPermission('system.settings.view') && (
                <NavDropdown.Item as={Link} to="/system-settings">
                  <i className="bi bi-sliders"></i>
                  <span className="ms-2">System Settings</span>
                </NavDropdown.Item>
              )}
              <NavDropdown.Item as={Link} to="/about">
                <i className="bi bi-info-circle"></i>
                <span className="ms-2">About</span>
              </NavDropdown.Item>
              <NavDropdown.Divider />
              <NavDropdown.Item onClick={() => handleThemeChange('light')}>
                <i className="bi bi-sun-fill"></i>
                <span className="ms-2">Light</span>
              </NavDropdown.Item>
              <NavDropdown.Item onClick={() => handleThemeChange('dark')}>
                <i className="bi bi-moon-stars-fill"></i>
                <span className="ms-2">Dark</span>
              </NavDropdown.Item>
              <NavDropdown.Item onClick={() => handleThemeChange('vapor')}>
                <i className="bi bi-palette"></i>
                <span className="ms-2">Vapor</span>
              </NavDropdown.Item>
              {hasPermission('users.view') && (
                <>
                  <NavDropdown.Divider />
                  <NavDropdown.Item as={Link} to="/users">
                    <i className="bi bi-person"></i>
                    <span className="ms-2">Users</span>
                  </NavDropdown.Item>
                </>
              )}
              <NavDropdown.Divider />
              <NavDropdown.Item onClick={handleResetSettings}>
                <i className="bi bi-arrow-counterclockwise"></i>
                <span className="ms-2">Reset Settings</span>
              </NavDropdown.Item>
            </NavDropdown>

            {/* separator */}
            <div className="nav-item py-2 py-lg-1 col-12 col-lg-auto">
              <div className="vr d-none d-lg-flex h-100 mx-lg-2 text-white"></div>
              <hr className="d-lg-none my-2 text-white-50" />
            </div>

            {currentWarehouse && (
              <NavDropdown
                align="end"
                data-bs-theme="light"
                title={
                  <span
                    title={`Current warehouse: ${currentWarehouse.name}`}
                    style={{
                      background: 'rgba(255,255,255,0.15)',
                      padding: '4px 10px',
                      borderRadius: '4px',
                      borderLeft: '3px solid white',
                    }}
                  >
                    <i className="bi bi-house-door me-2"></i>
                    <span style={{ fontWeight: 'bold' }}>
                      {getWarehouseAbbreviation(currentWarehouse.name)}
                    </span>
                  </span>
                }
                id="warehouse-selector-dropdown"
              >
                {warehouses.map((wh) => {
                  const whId = wh._id || wh.id;
                  const currentId = currentWarehouse?._id || currentWarehouse?.id;
                  return (
                    <NavDropdown.Item
                      key={whId}
                      onClick={() => setCurrentWarehouse(wh)}
                      active={currentId === whId}
                    >
                      {currentId === whId && <i className="bi bi-check me-2"></i>}
                      {wh.name}
                    </NavDropdown.Item>
                  );
                })}
              </NavDropdown>
            )}
          </Nav>
        </Navbar.Collapse>
      </div>
    </Navbar>
  );
}

export default Header;
